import { css } from 'styled-components';
import { math, rgba } from 'polished';
import { SDS_TEXT_BODY_SMALL_MEDIUM, SDS_TEXT_BODY_MEDIUM_MEDIUM, SDS_TEXT_BODY_LARGE_MEDIUM, SDS_TEXT_BODY_SMALL_REGULAR, SDS_TEXT_BODY_MEDIUM_REGULAR, SDS_TEXT_BODY_LARGE_REGULAR } from '@s-group/design-system-tokens/web/tokens/text.es6.js';
import { SDS_SIZE_BORDER_SMALL, SDS_SIZE_BORDER_LARGE, SDS_SIZE_LARGE, SDS_SIZE_XLARGE, SDS_SIZE_XXLARGE } from '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { SDS_SPACE_XSMALL, SDS_SPACE_SMALL, SDS_SPACE_MEDIUM, SDS_SPACE_XXXXSMALL, SDS_SPACE_XXSMALL, SDS_SPACE_XXXSMALL } from '@s-group/design-system-tokens/web/tokens/space.es6.js';
import { SDS_MISC_ALPHA_LARGE } from '@s-group/design-system-tokens/web/tokens/misc.es6.js';
import { SDS_ZINDEX_BASE } from '@s-group/design-system-tokens/web/tokens/zindex.es6.js';
import { scaledIcon } from '../common/util.js';
import { defaultProps } from './constants.js';
import { SDS } from '../common/namespace.js';

const tabListBackground = ({ theme, variant }) => variant === 'filled'
    ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_DEFAULT_NEUTRAL
    : 'transparent';
const tabListColumns = ({ alignment, $itemCount }) => {
    if (!$itemCount || $itemCount < 2) {
        return 'auto';
    }
    return (alignment &&
        {
            // Known `$itemCount` is needed here, because CSS grid `repeat()``
            // function doesn't support combining an automatic repetition and
            // intrinsic sizing like `min-content` or flexible sizing like `fr`
            // unit.
            // https://www.w3.org/TR/css3-grid-layout/#repeat-syntax
            //
            // `auto` sized first and/or last columns in the grid allow
            // `margin-left/right: auto` set in first and/or last tab list
            // items to push the tab list to left/center/right alignment.
            left: `repeat(${$itemCount - 1}, min-content) auto`,
            center: `auto repeat(${$itemCount - 2}, min-content) auto`,
            right: `auto repeat(${$itemCount - 1}, min-content)`,
            // `minmax(0, 1fr)` is used to enforce equal size of all tab list
            // items in all cases including focusing a tab with truncated text.
            full: `repeat(${$itemCount}, minmax(0, 1fr))`,
        }[alignment]);
};
const tabListGap = ({ sizing = defaultProps.sizing, variant }) => variant === 'filled'
    ? undefined
    : {
        small: SDS_SPACE_XSMALL,
        medium: SDS_SPACE_SMALL,
        large: SDS_SPACE_MEDIUM,
    }[sizing];
const tabListOverflow = ({ alignment }) => alignment === 'full' ? 'hidden' : 'auto';
const tabDirection = ({ icon, iconPos }) => !!icon && iconPos === 'top' ? 'column' : 'row';
const tabGap = ({ $iconOnly, icon }) => $iconOnly || !icon ? 0 : SDS_SPACE_XXXXSMALL;
const tabPadding = ({ $iconOnly = false, sizing = defaultProps.sizing, variant = defaultProps.variant, }) => ($iconOnly
    ? {
        // sizing: [vertical, horizontal]
        small: [SDS_SPACE_XXSMALL, SDS_SPACE_XXSMALL],
        medium: [SDS_SPACE_XSMALL, SDS_SPACE_XSMALL],
        large: [SDS_SPACE_SMALL, SDS_SPACE_SMALL],
    }[sizing]
    : {
        plain: {
            // sizing: [vertical, horizontal]
            small: [SDS_SPACE_XXSMALL, SDS_SPACE_XXXSMALL],
            medium: [SDS_SPACE_XXSMALL, SDS_SPACE_XXXSMALL],
            large: [SDS_SPACE_XSMALL, SDS_SPACE_XXXSMALL],
        },
        filled: {
            small: [SDS_SPACE_XXSMALL, SDS_SPACE_XXSMALL],
            medium: [SDS_SPACE_XXSMALL, SDS_SPACE_XSMALL],
            large: [SDS_SPACE_XSMALL, SDS_SPACE_SMALL],
        },
    }[variant][sizing]).join(' ');
const tabText = ({ selected, sizing = defaultProps.sizing, }) => (selected
    ? {
        small: SDS_TEXT_BODY_SMALL_MEDIUM,
        medium: SDS_TEXT_BODY_MEDIUM_MEDIUM,
        large: SDS_TEXT_BODY_LARGE_MEDIUM,
    }
    : {
        small: SDS_TEXT_BODY_SMALL_REGULAR,
        medium: SDS_TEXT_BODY_MEDIUM_REGULAR,
        large: SDS_TEXT_BODY_LARGE_REGULAR,
    })[sizing];
const tabTextColor = ({ color, theme, variant, }) => color === 'neutral' && variant !== 'filled'
    ? theme[SDS].colors.SDS_BRAND_COLOR_TEXT_STRONG_NEUTRAL
    : theme[SDS].colors.SDS_BRAND_COLOR_TEXT_DEFAULT_NEUTRAL;
const hoveredTabBackground = ({ color, theme, variant, }) => variant === 'filled'
    ? rgba(color === 'neutral'
        ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_NEUTRAL
        : theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_PRIMARY, SDS_MISC_ALPHA_LARGE)
    : undefined;
const hoveredTabBorderColor = ({ theme, variant, }) => variant === 'filled'
    ? 'transparent'
    : theme[SDS].colors.SDS_BRAND_COLOR_ELEMENT_MEDIUM_NEUTRAL;
const hoveredTabTextColor = ({ color, theme, variant, }) => color !== 'neutral' && variant === 'filled'
    ? theme[SDS].colors.SDS_BRAND_COLOR_ELEMENT_STRONG_PRIMARY
    : theme[SDS].colors.SDS_BRAND_COLOR_TEXT_DEFAULT_NEUTRAL;
const selectedTabBackground = ({ color, theme, variant, }) => variant === 'filled'
    ? color === 'neutral'
        ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_NEUTRAL
        : theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_PRIMARY
    : 'transparent';
const selectedTabBorderColor = ({ color, theme, }) => color === 'neutral'
    ? theme[SDS].colors.SDS_BRAND_COLOR_ELEMENT_DEFAULT_NEUTRAL
    : theme[SDS].colors.SDS_BRAND_COLOR_ELEMENT_STRONG_PRIMARY;
const selectedTabTextColor = ({ color, theme, variant, }) => color === 'neutral'
    ? theme[SDS].colors.SDS_BRAND_COLOR_TEXT_DEFAULT_NEUTRAL
    : // This is a temporary workaround for Yhteishyvä (SDS-1754)
        color === 'primary' &&
            variant === 'filled' &&
            theme[SDS].colors.SDS_BRAND_TAB_COLOR_TEXT_FILLED_PRIMARY
            ? theme[SDS].colors.SDS_BRAND_TAB_COLOR_TEXT_FILLED_PRIMARY
            : theme[SDS].colors.SDS_BRAND_COLOR_TEXT_STRONG_PRIMARY;
const iconMargin = ({ $iconOnly, iconPos, sizing = defaultProps.sizing, }) => {
    if ($iconOnly || iconPos === 'top') {
        return 0;
    }
    const textStyle = tabText({ sizing });
    return scaledIcon([`${textStyle.fontSize}`, `${textStyle.lineHeight}`])
        .iconMargin;
};
const iconSize = ({ $iconOnly, iconPos, sizing = defaultProps.sizing, }) => {
    if ($iconOnly || iconPos === 'top') {
        // Use fixed sizes
        return {
            small: SDS_SIZE_LARGE,
            medium: SDS_SIZE_XLARGE,
            large: SDS_SIZE_XXLARGE,
        }[sizing];
    }
    // Scale size to match with text
    const textStyle = tabText({ sizing });
    return scaledIcon([`${textStyle.fontSize}`, `${textStyle.lineHeight}`])
        .iconSize;
};
// Width of a single focus outline
const focusOutlineSingleWidth = SDS_SIZE_BORDER_LARGE;
// Total width consumed by focus outlines (primary + inverse)
const focusOutlineTotalWidth = math(`2 * ${focusOutlineSingleWidth}`);
const TabListStyle = css `
    display: flex;
    margin: ${math(`-1 * ${focusOutlineTotalWidth}`)} 0;
    overflow: ${tabListOverflow} hidden;
    padding: ${focusOutlineTotalWidth} 0;

    [role='tablist'] {
        background: ${tabListBackground};
        box-shadow: inset 0 ${math(`-1 * ${SDS_SIZE_BORDER_SMALL}`)} 0
            ${({ theme }) => theme[SDS].colors.SDS_BRAND_COLOR_BORDER_WEAK_NEUTRAL};
        display: grid;
        gap: ${tabListGap};
        grid-auto-flow: column;
        grid-template-columns: ${tabListColumns};
        width: 100%;
    }
`;
const TabListItemStyle = css `
    ${tabText};
    align-items: center;
    background: none;
    border: none;
    border-radius: 0;
    box-sizing: border-box;
    color: ${tabTextColor};
    cursor: pointer;
    display: flex;
    flex-direction: ${tabDirection};
    gap: ${tabGap};
    justify-content: center;
    padding: ${tabPadding};

    &:hover {
        background: ${hoveredTabBackground};
        box-shadow: inset 0 ${math(`-1 * ${SDS_SIZE_BORDER_LARGE}`)} 0
            ${hoveredTabBorderColor};
        color: ${hoveredTabTextColor};
        padding: ${props => tabPadding({
    ...props,
    selected: true, // compensate border
})};
    }

    &:active,
    &[aria-selected='true'] {
        ${props => tabText({
    ...props,
    selected: true, // trigger selected state on &:active
})};
        background: ${selectedTabBackground};
        box-shadow: inset 0 ${math(`-1 * ${SDS_SIZE_BORDER_LARGE}`)} 0
            ${selectedTabBorderColor};
        color: ${selectedTabTextColor};
    }

    &:focus-visible {
        outline: none;
        position: relative;
        z-index: ${SDS_ZINDEX_BASE};

        &::before {
            box-shadow: inset 0 0 0 ${focusOutlineTotalWidth}
                ${({ theme }) => theme[SDS].colors.SDS_BRAND_COLOR_BORDER_INVERSE_NEUTRAL};
            content: '';
            inset: ${math(`-1 * ${focusOutlineTotalWidth}`)} 0;
            outline: ${focusOutlineSingleWidth} solid
                ${({ theme }) => theme[SDS].colors.SDS_BRAND_COLOR_BORDER_STRONG_PRIMARY};
            outline-offset: ${math(`-1 * ${focusOutlineSingleWidth}`)};
            position: absolute;
        }
    }

    &:first-child {
        margin-left: ${({ alignment }) => alignment === 'right' || alignment === 'center'
    ? 'auto'
    : undefined};
    }

    &:last-child {
        margin-right: ${({ alignment }) => alignment === 'left' || alignment === 'center'
    ? 'auto'
    : undefined};
    }

    .tab-icon {
        box-sizing: content-box;
        flex-shrink: 0;
        height: ${iconSize};
        margin: ${iconMargin};
        width: ${iconSize};

        svg {
            height: 100%;
            width: 100%;
        }
    }

    .tab-label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;
// Tab panel container
const TabStyle = css ``;
// Tabs container
const TabsStyle = css ``;

export { TabListItemStyle, TabListStyle, TabStyle, TabsStyle };
//# sourceMappingURL=Tabs.styles.js.map
